import React, { useState, useRef, useEffect, Suspense } from 'react';
import { motion } from 'framer-motion';
import { Canvas } from '@react-three/fiber';
import './MainContent.css';
import SimpleSphereModel from './SimpleSphereModel'; // Import the simpler sphere model

const Hero = ({ navigateToWiggaPaper }) => {
  const counterRefs = useRef([]);
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [brokeWiggaDetected, setBrokeWiggaDetected] = useState(false);
  const [brokeScore, setBrokeScore] = useState(0);
  const contractTickerRef = useRef(null);
  
  const contractAddress = "Hr1KCdpK4L3zVzvK6ntpEPfwVmfrXgMKfBfpdXeMpump";
  
  // Copy contract address to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(contractAddress).then(() => {
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
    });
  };
  
  // Broke Wigga Detection System
  const detectBrokeWigga = () => {
    // Generate a random broke score from 0 to 100
    const newBrokeScore = Math.floor(Math.random() * 101);
    setBrokeScore(newBrokeScore);
    setBrokeWiggaDetected(true);
  };
  
  // Close broke wigga modal
  const closeModal = () => {
    setBrokeWiggaDetected(false);
  };
  
  // Animated counter effect
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    };
    
    const handleIntersect = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const target = entry.target;
          const finalValue = parseInt(target.getAttribute('data-value'), 10);
          const duration = 2000; // 2 seconds
          const steps = 50;
          const stepTime = duration / steps;
          
          let currentValue = 0;
          const increment = finalValue / steps;
          
          const timer = setInterval(() => {
            currentValue += increment;
            if (currentValue >= finalValue) {
              target.textContent = target.hasAttribute('data-prefix') 
                ? `${target.getAttribute('data-prefix')}${finalValue.toLocaleString()}` 
                : finalValue.toLocaleString();
              clearInterval(timer);
            } else {
              target.textContent = target.hasAttribute('data-prefix') 
                ? `${target.getAttribute('data-prefix')}${Math.floor(currentValue).toLocaleString()}` 
                : Math.floor(currentValue).toLocaleString();
            }
          }, stepTime);
          
          observer.unobserve(target);
        }
      });
    };
    
    const observer = new IntersectionObserver(handleIntersect, options);
    
    // Observe all counter elements
    counterRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });
    
    return () => {
      if (observer) {
        counterRefs.current.forEach((ref) => {
          if (ref) observer.unobserve(ref);
        });
      }
    };
  }, []);

  // Add counter ref
  const addToRefs = (el) => {
    if (el && !counterRefs.current.includes(el)) {
      counterRefs.current.push(el);
    }
  };
  
  // Get joke based on broke score
  const getBrokeJoke = () => {
    const jokes = [
      "You're so broke, you can't even afford to pay attention to your portfolio.",
      "You're so broke, your wallet has an OnlyFans account just to make ends meet.",
      "You're so broke, your credit score is just a middle finger emoji.",
      "You're so broke, your bank account filed for emotional bankruptcy.",
      "You're so broke, your net worth is in scientific notation... with a negative exponent.",
      "You're so broke, your bank statements come with a free box of tissues.",
      "You're so broke, your NFTs are drawn in MS Paint and stored on a floppy disk.",
      "You're so broke, your crypto wallet just sends you condolence emails.",
      "You're so broke, when you open your wallet moths fly out and they're asking YOU for loans.",
      "You're so broke, your wallet has PTSD from all those $3 transactions.",
      "You're so broke, Nigerian princes email you offering financial assistance.",
      "You're so broke, your portfolio is just a sad Discord server with zero members.",
      "You're so fucking broke, you use the free wifi at McDonald's to trade shitcoins.",
      "You're so goddamn broke, your portfolio chart looks like a journey to the center of the earth.",
      "You're so broke, your hardware wallet is a piece of paper in a Ziploc bag."
    ];
    
    return jokes[Math.floor(Math.random() * jokes.length)];
  };
  
  // Get financial advice based on broke score
  const getBrokeAdvice = () => {
    const advice = [
      "RECOMMENDED ACTION: Sell your fucking organs, they're regenerating anyway, right?",
      "RECOMMENDED ACTION: Divorce your spouse so you can ape in with the child support money.",
      "RECOMMENDED ACTION: Max out every credit card you own to buy more $WIGGA. It's basic math.",
      "RECOMMENDED ACTION: Skip your rent payment this month. Your landlord can wait, gains can't.",
      "RECOMMENDED ACTION: Tell your boss to go fuck themselves and put your entire 401k into $WIGGA.",
      "RECOMMENDED ACTION: Pawn your grandmother's wedding ring. She's not using it anymore.",
      "RECOMMENDED ACTION: Convert your life savings to $WIGGA tokens before we leave your broke ass behind.",
      "RECOMMENDED ACTION: Stop buying food. Your body has stored fat for this exact scenario.",
      "RECOMMENDED ACTION: Convince your parents that $WIGGA is their retirement plan now."
    ];
    
    return advice[Math.floor(Math.random() * advice.length)];
  };
  
  return (
    <section className="hero-section" style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Video Background - Cover the entire hero section */}
      <video 
        autoPlay 
        loop 
        muted 
        className="background-video"
        style={{
          position: 'absolute',
          minWidth: '100%',
          minHeight: '100%',
          width: 'auto',
          height: 'auto',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          objectFit: 'cover',
          zIndex: 0
        }}
        onError={(e) => console.error("Video error:", e)}
      >
        <source src="/Untitled design (27).mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      
      {/* Scrolling Contract Address Ticker */}
      <div className="scrolling-contract-ticker" onClick={copyToClipboard} style={{ position: 'relative', zIndex: 5 }}>
        <div className="ticker-track">
          <div className="ticker-content">
            <span className="ticker-item">CONTRACT: <span className="address">{contractAddress}</span></span>
            <span className="ticker-item">CONTRACT: <span className="address">{contractAddress}</span></span>
            <span className="ticker-item">CONTRACT: <span className="address">{contractAddress}</span></span>
            <span className="ticker-item">CONTRACT: <span className="address">{contractAddress}</span></span>
          </div>
        </div>
        <div className={`copy-notification ${copiedAddress ? 'show' : ''}`}>
          Address Copied! Now don't fucking lose it!
        </div>
      </div>
      
      <div className="hero-content" style={{ position: 'relative', zIndex: 5 }}>
        {/* Content Card for better readability */}
        <motion.div 
          className="content-card"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            padding: '2rem',
            borderRadius: '10px',
            boxShadow: '0 0 20px rgba(0, 255, 153, 0.3)',
            maxWidth: '800px',
            margin: '0 auto',
            border: '1px solid rgba(0, 255, 153, 0.5)',
            backdropFilter: 'blur(5px)'
          }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.div 
            className="hero-text"
          >
            <h1 className="hero-title">
              <span className="gradient-text">WiggaChain</span>
              <span className="subtitle-text" style={{ color: 'white' }}>Making Degens Filthy Fucking Rich</span>
            </h1>
            
            {/* 3D Sphere Container */}
            <div className="sphere-container">
              <Canvas camera={{ position: [0, 0, 5], fov: 45 }} alpha={true}>
                <ambientLight intensity={0.8} />
                <pointLight position={[10, 10, 10]} intensity={1} />
                <Suspense fallback={null}>
                  <SimpleSphereModel />
                </Suspense>
              </Canvas>
            </div>
            
            <p className="hero-description" style={{ color: 'white' }}>
              Our blockchain doesn't just process transactions - it processes FINANCIAL FREEDOM, bitch. 
              Built with quantum algorithms so complex even we don't understand that shit, but fuck it.
              No KYC, no SEC, no fucking problems - just pure, unadulterated, face-melting gains that'll 
              make your wife leave her boyfriend for YOU.
            </p>
            
            <div className="hero-buttons">
              <button 
                className="primary-button" 
                onClick={detectBrokeWigga}
                style={{
                  backgroundColor: 'black',
                  color: '#ffffff',
                  border: '2px solid #00ff99',
                  position: 'relative',
                  zIndex: 5
                }}
              >
                <span style={{ color: 'white', fontWeight: 'bold' }}>Broke Wigga Detector</span>
                <span className="button-glow"></span>
              </button>
              
              <a href="https://raydium.io/swap/" target="_blank" rel="noopener noreferrer" className="buy-button">
                <span>💰 APE IN NOW OR STAY POOR</span>
                <span className="fire-emoji">🔥🔥🔥</span>
              </a>
              
              <button className="secondary-button" onClick={navigateToWiggaPaper}>
                <span>Read WiggaPaper</span>
              </button>
            </div>
          </motion.div>
          
          <motion.div 
            className="hero-stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <div className="stat-card">
              <div className="stat-value" ref={addToRefs} data-value="69420" data-prefix="$" style={{ color: '#00ff99' }}>$0</div>
              <div className="stat-label" style={{ color: 'white' }}>Million TVL</div>
            </div>
            
            <div className="stat-card">
              <div className="stat-value" ref={addToRefs} data-value="42069" style={{ color: '#00ff99' }}>0</div>
              <div className="stat-label" style={{ color: 'white' }}>Lambos Purchased</div>
            </div>
            
            <div className="stat-card">
              <div className="stat-value" ref={addToRefs} data-value="100" style={{ color: '#00ff99' }}>0</div>
              <div className="stat-label" style={{ color: 'white' }}>% Gains Guaranteed</div>
            </div>
            
            <div className="stat-card">
              <div className="stat-value" ref={addToRefs} data-value="0" style={{ color: '#00ff99' }}>0</div>
              <div className="stat-label" style={{ color: 'white' }}>Rugs Pulled</div>
            </div>
          </motion.div>
        </motion.div>
      </div>
      
      {/* Broke Wigga Detection Modal */}
      {brokeWiggaDetected && (
        <div className="broke-wigga-modal" style={{ zIndex: 100 }}>
          <div className="modal-content">
            <button className="modal-close-btn" onClick={closeModal}>✕</button>
            <h2>BROKE WIGGA DETECTION SYSTEM™</h2>
            <div className="broke-meter">
              <div className="broke-meter-fill" style={{ width: `${brokeScore}%` }}></div>
            </div>
            <div className="broke-score">Broke Score: {brokeScore}/100</div>
            <div className="broke-diagnosis">
              {brokeScore < 50 ? (
                <>
                  <h3>DIAGNOSIS: CERTIFIED BROKE ASS MOTHERFUCKER</h3>
                  <p>{getBrokeJoke()}</p>
                  <p className="broke-action">{getBrokeAdvice()}</p>
                </>
              ) : (
                <>
                  <h3>DIAGNOSIS: POTENTIAL WIGGA WHALE (BUT STILL PRETTY FUCKING BROKE)</h3>
                  <p>{getBrokeJoke()}</p>
                  <p className="broke-action">{getBrokeAdvice()}</p>
                </>
              )}
            </div>
            <button onClick={closeModal} className="close-modal-btn">
              Accept My Pathetic Financial Reality
            </button>
          </div>
        </div>
      )}
      
      <div className="scroll-indicator" style={{ position: 'relative', zIndex: 5 }}>
        <div className="scroll-text" style={{ color: 'white', textShadow: '0 0 5px rgba(0,0,0,0.9)' }}>Keep Scrolling, Future Millionaire</div>
        <div className="scroll-arrow"></div>
      </div>
    </section>
  );
};

export default Hero;